import { OTP_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { cn } from "@sagot/ui";
import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

export interface OtpInputProps {
  /** Digits entered so far (index i = box i; blanks are spaces). */
  value: string;
  onChange: (value: string) => void;
  /** id of the first box (for the visible `<label htmlFor>`). */
  id: string;
  length?: number;
  autoFocus?: boolean;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
}

const toDigits = (value: string, length: number) => Array.from({ length }, (_, i) => value[i] ?? " ");
const DIGIT = /\d/;

/**
 * `[data-otp]` (auth.js): digits only, auto-advance, Backspace on an empty box clears and focuses
 * the previous one, ←/→ move, focus selects, paste spreads up to `length` digits.
 */
export function OtpInput({ value, onChange, id, length = OTP_LENGTH, autoFocus, invalid, describedBy, disabled }: OtpInputProps) {
  const { t } = useTranslation();
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const digits = toDigits(value, length);
  const emit = (next: string[]) => {
    onChange(next.join("").replace(/\s+$/, ""));
  };
  const focus = (index: number) => {
    boxes.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  };

  const onInput = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit || " ";
    emit(next);
    if (digit) focus(index + 1);
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !DIGIT.test(digits[index] ?? "") && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = " ";
      emit(next);
      focus(index - 1);
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focus(index - 1);
    } else if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      focus(index + 1);
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    event.preventDefault();
    emit(toDigits(pasted, length));
    focus(Math.min(pasted.length, length - 1));
  };

  return (
    <div className="grid grid-cols-6 gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(node) => {
            boxes.current[index] = node;
          }}
          id={index === 0 ? id : undefined}
          className={cn("input h-12 px-0 text-center font-mono text-lg font-semibold", invalid && "border-danger")}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={t("auth.otp.digit", { n: index + 1 })}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          // Autofocus is part of the mockup flow: the code is the only task on the page.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus && index === 0}
          disabled={disabled}
          value={digit.trim()}
          onChange={(event) => {
            onInput(index, event.target.value);
          }}
          onKeyDown={(event) => {
            onKeyDown(index, event);
          }}
          onFocus={(event) => {
            event.target.select();
          }}
          onPaste={onPaste}
        />
      ))}
    </div>
  );
}

/** Six digits entered. */
export function isCompleteOtp(value: string, length = OTP_LENGTH): boolean {
  return new RegExp(`^\\d{${String(length)}}$`).test(value);
}
